import React, { useCallback, useState, useRef } from 'react';
import { UploadIcon, FileTextIcon, XCircleIcon, CheckCircleIcon, Loader2Icon } from 'lucide-react';
import { LoanDocument } from '../types/loan.types';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { useFileUpload } from '../hooks/useFileUpload';
interface DocumentSlotProps {
  document: LoanDocument;
  onUpload: (documentId: string, file: File, fileName: string) => void;
}
// Status-specific styling configuration
const statusStyles = {
  missing: {
    borderColor: 'border-red-200',
    bgColor: 'bg-red-50/30',
    hoverBorder: 'hover:border-red-300'
  },
  uploaded: {
    borderColor: 'border-blue-300',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-900'
  },
  pending_review: {
    borderColor: 'border-amber-300',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-900'
  },
  approved: {
    borderColor: 'border-emerald-300',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-900'
  }
};
export function DocumentSlot({
  document,
  onUpload
}: DocumentSlotProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    isUploading,
    progress,
    error,
    uploadFile,
    resetState
  } = useFileUpload({
    onSuccess: file => {
      onUpload(document.id, file, file.name);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    }
  });
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  }, [uploadFile]);
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  }, [uploadFile]);
  const handleClick = useCallback(() => {
    if (!isUploading && document.status === 'missing') {
      fileInputRef.current?.click();
    }
  }, [isUploading, document.status]);
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isUploading && document.status === 'missing') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [isUploading, document.status]);
  const isUploadable = document.status === 'missing';
  const styles = statusStyles[document.status];
  return <div className={`space-y-3 p-3 rounded-lg border-2 ${styles.borderColor} ${styles.bgColor} transition-all duration-200`} data-id="element-21">
      <div className="flex items-center justify-between gap-3" data-id="element-22">
        <span className="text-sm font-semibold text-gray-900" data-id="element-23">
          {document.name}
        </span>
        <DocumentStatusBadge status={document.status} data-id="element-24" />
      </div>

      {isUploadable ? <div role="button" tabIndex={0} onClick={handleClick} onKeyDown={handleKeyDown} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`
            relative flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed
            transition-all duration-200 cursor-pointer min-h-[80px]
            ${isDragOver ? 'border-blue-400 bg-blue-100 scale-[1.02]' : `${styles.borderColor} bg-white ${styles.hoverBorder} hover:bg-red-50`}
            ${isUploading ? 'pointer-events-none' : ''}
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          `} aria-label={`Upload ${document.name}. Drag and drop or click to browse.`} data-id="element-25">
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" aria-hidden="true" data-id="element-26" />

          {isUploading ? <div className="flex flex-col items-center gap-2" data-id="element-27">
              <Loader2Icon className="w-6 h-6 text-blue-500 animate-spin" data-id="element-28" />
              <span className="text-xs text-blue-600 font-medium" data-id="element-29">
                Uploading... {progress}%
              </span>
            </div> : showSuccess ? <div className="flex flex-col items-center gap-2 animate-pulse" data-id="element-30">
              <CheckCircleIcon className="w-6 h-6 text-emerald-500" data-id="element-31" />
              <span className="text-xs text-emerald-600 font-medium" data-id="element-32">
                Uploaded!
              </span>
            </div> : <>
              <UploadIcon className={`w-6 h-6 mb-1 ${isDragOver ? 'text-blue-500' : 'text-red-400'}`} data-id="element-33" />
              <span className="text-xs text-gray-600 text-center font-medium" data-id="element-34">
                Drop file or{' '}
                <span className="text-blue-600 font-semibold" data-id="element-35">browse</span>
              </span>
            </>}
        </div> : <div className={`flex items-center gap-3 p-3 rounded-lg bg-white border-2 ${styles.borderColor}`} data-id="element-36">
          <FileTextIcon className={`w-5 h-5 ${styles.textColor} flex-shrink-0`} data-id="element-37" />
          <div className="flex-1 min-w-0" data-id="element-38">
            <p className={`text-sm font-medium ${styles.textColor} truncate`} data-id="element-39">
              {document.fileName || 'Document uploaded'}
            </p>
            {document.uploadedDate && <p className="text-xs text-gray-600 mt-0.5" data-id="element-40">
                {document.uploadedDate}
              </p>}
          </div>
        </div>}

      {error && <div className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-100" data-id="element-41">
          <XCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" data-id="element-42" />
          <span className="text-xs text-red-600 flex-1" data-id="element-43">{error}</span>
          <button onClick={() => {
        resetState();
        fileInputRef.current?.click();
      }} className="text-xs text-red-600 font-medium hover:text-red-700 underline" data-id="element-44">
            Retry
          </button>
        </div>}
    </div>;
}